/**
 * The 24 superadmin modules from Docs/08-SUPERADMIN-SPEC.md, plus Topics (A-25),
 * Business claims (A-26) and Business submissions (A-27), which come from client
 * documents the spec predates.
 *
 * This is the single registry the sidebar, the command palette, the router and
 * (later) the route guards all read from. Adding a module means adding a row
 * here plus its `modules.<id>` translation keys — nothing else.
 */
import {
  Banknote,
  BarChart3,
  Bell,
  BookOpen,
  Building2,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  CreditCard,
  FileClock,
  FileText,
  Flag,
  Gauge,
  Handshake,
  Hash,
  LayoutDashboard,
  LifeBuoy,
  Languages,
  Layers,
  Megaphone,
  Scale,
  Settings2,
  ShieldCheck,
  Sparkles,
  Star,
  Tags,
  ToggleLeft,
  Users,
  UserCog,
  type LucideIcon,
} from 'lucide-react'

/** Admin roles, per the Roles table in the spec. */
export const ADMIN_ROLES = ['super_admin', 'moderator', 'support', 'finance', 'content'] as const
export type AdminRole = (typeof ADMIN_ROLES)[number]

export const NAV_GROUPS = [
  'operations',
  'moderation',
  'monetization',
  'content',
  'platform',
] as const
export type NavGroup = (typeof NAV_GROUPS)[number]

export type AdminModule = {
  /** Stable id; also the i18n key under `modules.*`. */
  id: string
  /** Spec reference, e.g. `A-01`. Shown on placeholder screens. */
  code: string
  /** Route path relative to the shell root. */
  path: string
  icon: LucideIcon
  group: NavGroup
  /** Roles allowed to open the module. Enforced by route guards *and* RLS. */
  roles: readonly AdminRole[]
  /** Extra search terms for the command palette (not displayed). */
  keywords: readonly string[]
  /**
   * Is this module actually finished?
   *
   * False means it still routes to the shared placeholder. In demo mode
   * (VITE_DEMO_MODE=true, the client-facing deployment) unfinished modules are
   * hidden from the sidebar and the command palette, so the client is never
   * shown a menu of empty rooms. See packages/config/src/demo-mode.ts.
   */
  built: boolean
}

const ALL: readonly AdminRole[] = ADMIN_ROLES

export const MODULES: readonly AdminModule[] = [
  // ── Operations ────────────────────────────────────────────────────────────
  {
    id: 'dashboard',
    code: 'A-01',
    path: '/',
    icon: LayoutDashboard,
    group: 'operations',
    roles: ALL,
    keywords: ['home', 'kpi', 'overview', 'metrics', 'start'],
    built: true,
  },
  {
    id: 'users',
    code: 'A-02',
    path: '/users',
    icon: Users,
    group: 'operations',
    roles: ['super_admin', 'moderator', 'support'],
    keywords: ['accounts', 'people', 'customers', 'personal', 'gdpr', 'impersonate'],
    built: true,
  },
  {
    id: 'businesses',
    code: 'A-03',
    path: '/businesses',
    icon: Building2,
    group: 'operations',
    roles: ['super_admin', 'moderator', 'support'],
    keywords: ['companies', 'vendors', 'org number', 'profiles'],
    built: true,
  },
  {
    id: 'requests',
    code: 'A-09',
    path: '/requests',
    icon: ClipboardList,
    group: 'operations',
    roles: ['super_admin', 'moderator', 'support'],
    // No 'recruitment': the kind is retired (Docs/23 § 11b) and these keywords
    // drive the command palette, so searching it would offer a screen that
    // deliberately returns none of it.
    keywords: ['jobs', 'tenders', 'offers', 'matching'],
    built: true,
  },
  {
    id: 'support',
    code: 'A-19',
    path: '/support',
    icon: LifeBuoy,
    group: 'operations',
    /*
     * ⚠️ THE ONE MODULE IN THIS FILE WHERE `roles` IS ALSO THE BOUNDARY.
     *
     * Everywhere else in this panel the list is navigation and RLS is the boundary,
     * because the write policy is a bare `is_admin()` — a moderator who can block a
     * topic genuinely can also suspend a business. Migration 052 narrowed EVERY
     * support policy to `is_admin(array['super_admin','support'])`, deliberately: a
     * ticket is free text from somebody with a problem and holds names, addresses,
     * order details and occasionally a password somebody should not have typed. A
     * `finance` or `content` operator has no reason to read it.
     *
     * So the two lists agree here, and a `moderator` opening this screen sees an
     * empty queue and a refused write. Both are correct, and the screen says so.
     */
    roles: ['super_admin', 'support'],
    /*
     * ⚠️ `built: true` FOR DELIBERATELY LESS THAN DOCS/08 A-19 ASKS FOR, AND THE
     * MISSING HALF IS SCOPE RATHER THAN UNFINISHED WORK.
     *
     * The client NEVER ASKED FOR SUPPORT and never refused it either — A-19 is ours,
     * and it is built under the Docs/15 Q81 rule: *if the client explicitly said no,
     * do not build it; if it was merely absent, build it.* A-20 Disputes is the other
     * side of that rule and was removed from this file on the same day.
     *
     * It is therefore unbilled scope, and small on purpose: a queue, a transcript, a
     * reply, three triage controls and hard rule 10's soft delete. What A-19 asks for
     * and this does not do — an SLA clock, contact-form submissions, attachments — is
     * listed in migration 052's header and stated on the screen. "Canned responses"
     * has its TABLE (hard rule 4: the list is data) and no editor.
     */
    keywords: ['tickets', 'help', 'contact', 'sla', 'inbox'],
    built: true,
  },
  {
    id: 'disputes',
    code: 'A-20',
    path: '/disputes',
    icon: Scale,
    group: 'operations',
    /*
     * ⚠️ RESTORED ON 6 OCT AFTER BEING DELETED ON 1 OCT, AND THE HISTORY IS WORTH
     * KEEPING RATHER THAN TIDYING AWAY.
     *
     * Commit `b3435d7` removed this row, recording that *"the client refused it in
     * writing"* — Docs/22 § 3 listed disputes under "explicitly rejected in the
     * text and therefore not to be built", and the developer's scope rule
     * (Docs/15 Q81) said not to build it. **He had not refused it.** The October
     * review heads § 2.4 **"Disputes – Needs to Be Built"**, § 2.3 puts it on page
     * 2 of the Support interface, and his Telegram asks whether a
     * pay-with-Proffpenger function with a dispute on disagreement fits the launch
     * timeframe. Two of the three predate or coincide with the deletion.
     *
     * ✅ The deletion cost a few hours of UI and nothing else: `disputes` and
     * `dispute_messages` had never existed as tables, so there was no migration to
     * revert and no data to restore. The schema arrived with migrations 057/058.
     *
     * ⚠️ `roles` IS A REAL BOUNDARY HERE, AS IN A-19 AND ALMOST NOWHERE ELSE IN
     * THIS FILE. Everywhere else the list is navigation and the policy is a bare
     * `is_admin()`. Migration 058 narrowed EVERY dispute policy to
     * `is_admin(array['super_admin','support'])`, and `finance` is deliberately
     * absent even though a dispute sits next to money: it is a complaint about two
     * named people's conduct, and once settlement exists what a finance operator
     * needs is the settlement record, not the argument. So a `moderator`,
     * `finance` or `content` operator sees an empty queue and a refused write.
     * Both are correct, and the screen says so.
     */
    roles: ['super_admin', 'support'],
    /*
     * 🔴 `built: true` FOR THE RECORD AND THE VERDICT, AND FOR NO MONEY AT ALL.
     *
     * § 2.4 describes escrow — *customer pays → platform holds the money → job
     * completed → both confirm or disagree* — and its own first sentence is the
     * scope: *"The Admin Panel should be **prepared for a future**
     * dispute-resolution system."* There are no balances, no payouts, no holds and
     * no payment record anywhere in this schema, so the module has no "release the
     * payment" control and must never grow one. Whether Proffplassen ever holds
     * client funds is Docs/15 Q96, an open COMMERCIAL question with legal weight
     * in Norway, and he asked directly whether it fits the launch timeframe — a
     * question that needs an honest answer, not a button.
     *
     * ⚠️ AND A VERDICT DOES NOT TOUCH THE JOB. `job_status` carries a `disputed`
     * value and nothing writes it from here.
     *
     * Also absent, and listed in migration 058's header rather than here: a
     * private `dispute-evidence` storage bucket, so evidence is a URL with nothing
     * behind it; and any notification, because none of the three senders can send.
     */
    keywords: ['dispute', 'disagreement', 'complaint', 'refund', 'verdict', 'escalated', 'uenighet'],
    built: true,
  },

  // ── Moderation ────────────────────────────────────────────────────────────
  {
    id: 'verification',
    code: 'A-06',
    path: '/verification',
    icon: ShieldCheck,
    group: 'moderation',
    roles: ['super_admin', 'moderator'],
    /*
     * ⚠️ `built: true` WITH THREE OF DOCS/08 A-06'S FOUR TABS, AND NEITHER THE
     * SLA NOR THE BRØNNØYSUND COMPARISON. "Business documents" has no table of
     * any kind and the schema has no storage bucket at all; nothing records WHEN
     * a subject asked, so the 48 h SLA is not computable from any column that
     * exists; and `verify-org-number` is written, tested and NOT DEPLOYED (404
     * on this project). The screen names every one of these where the missing
     * control would have been, rather than here.
     */
    keywords: ['identity', 'documents', 'bronnoysund', 'kyc', 'certifications', 'queue'],
    built: true,
  },
  {
    /*
     * Business claims — the ACCESS half of Docs/29 § 3, and the queue migration
     * 045 was written for. Before this screen a filed claim could only be
     * approved from SQL.
     *
     * NOT one of the 24 modules in Docs/08-SUPERADMIN-SPEC.md: it comes from the
     * client's 26 Sep clarifications, which the spec predates, so it takes the
     * next free code rather than renumbering. `SPEC_MODULE_COUNT` below still
     * counts the spec's 24 on purpose — this row is not one of them.
     *
     * ⚠️ It sits beside Verification (A-06) and NOT inside it, because Docs/29 § 3
     * insists the two are different systems. A-06 answers *how much do we trust
     * this business* — badges, eligibility, the organisation register. This
     * answers *may this person act for this business at all*, and it must stay
     * light: *"We should not put every verification requirement in front of the
     * user before they can access the app."* Merging them would be the fastest way
     * to lose that distinction.
     *
     * `roles` is the same trio as Verification. `review_business_claim()` itself
     * only requires a bare `is_admin()`, so this list is navigation rather than a
     * boundary — as everywhere else in this file, RLS is the boundary.
     */
    id: 'claims',
    code: 'A-26',
    path: '/claims',
    icon: Handshake,
    group: 'moderation',
    roles: ['super_admin', 'moderator', 'support'],
    keywords: ['claim', 'claimed', 'listed', 'trust state', 'ownership', 'onboarding', 'access'],
    built: true,
  },
  {
    /*
     * Business submissions — the OTHER half of Docs/29 § 2's admin queue, and the
     * screen migration 048 leaves as the only legitimate way to make a business
     * `active`. Before it, a business submitted for review could only be approved
     * from SQL: A-03 could set the status from a four-value dropdown, but that is a
     * general-purpose control on a directory screen with no third verdict, no
     * queue ordering, and rejection in another tab behind a typed slug.
     *
     * NOT one of the 24 modules in Docs/08-SUPERADMIN-SPEC.md — like A-25 and
     * A-26 it comes from a client document the spec predates, so it takes the next
     * free code rather than renumbering. `SPEC_MODULE_COUNT` below still counts the
     * spec's 24 on purpose.
     *
     * ⚠️ It sits beside Claims (A-26) and Verification (A-06) and inside NEITHER.
     * Docs/29 § 3 keeps three questions apart: *may this business be published*
     * (here), *may this person act for it* (A-26), *how much do we trust it*
     * (A-06). Approving here grants no membership and awards no badge.
     *
     * `roles` is the same trio as Claims. The write is a plain `businesses` UPDATE
     * whose policy is `has_business_capability(...) OR is_admin()`, so this list is
     * navigation rather than a boundary — as everywhere else in this file, RLS and
     * `protect_business_status()` are the boundary.
     */
    id: 'submissions',
    code: 'A-27',
    path: '/submissions',
    icon: ClipboardCheck,
    group: 'moderation',
    roles: ['super_admin', 'moderator', 'support'],
    keywords: [
      'pending review',
      'approve',
      'reject',
      'publish',
      'queue',
      'onboarding',
      'awaiting review',
      'submitted',
    ],
    built: true,
  },
  {
    id: 'moderation',
    code: 'A-07',
    path: '/moderation',
    icon: Flag,
    group: 'moderation',
    roles: ['super_admin', 'moderator'],
    /*
     * ⚠️ `built: true` COVERS THE REPORTED-ITEMS QUEUE AND NOTHING ELSE.
     * Docs/08 A-07 names six queues — Posts · Reviews · Requests · Media ·
     * Comments · Reported items — and only the last is a table. Reviews and
     * requests have their own modules (A-08, A-09); `review_media_write` and
     * the `post_comments` write policy have no `is_admin()` branch at all, so
     * media and comments cannot be moderated from any client. The screen says
     * all of this on itself rather than here.
     */
    keywords: ['reports', 'posts', 'media', 'comments', 'abuse', 'strikes'],
    built: true,
  },
  {
    id: 'reviews',
    code: 'A-08',
    path: '/reviews',
    icon: Star,
    group: 'moderation',
    roles: ['super_admin', 'moderator'],
    /*
     * ⚠️ `built: true` WITH TWO OF DOCS/08 A-08'S ACTIONS MISSING, BOTH BECAUSE
     * THE SCHEMA REFUSES THEM. "mark verified/unverified" is overwritten by the
     * `reviews_set_verified` BEFORE UPDATE trigger on every write, and the
     * velocity watchlist is a grouped aggregate PostgREST cannot express. The
     * screen names both where the controls would have been.
     */
    keywords: ['ratings', 'stars', 'fake', 'watchlist', 'responses'],
    built: true,
  },

  // ── Monetization ──────────────────────────────────────────────────────────
  {
    id: 'sponsored',
    code: 'A-10',
    path: '/sponsored',
    icon: Sparkles,
    group: 'monetization',
    roles: ['super_admin', 'finance'],
    /*
     * ✅ BUILT 8 OCT, AND IT IS **NOT** THE FRIDAY AUCTION. The sentence above
     * this one said "deliberately still unbuilt" for a day; what changed is the
     * half that was genuinely missing.
     *
     * 🔴 THE AUCTION IS STILL NOT BUILT, AND THIS ROW'S OWN KEYWORDS STILL SAY
     * "calendar" AND "bidding" BECAUSE THAT IS WHAT A-10 WILL EVENTUALLY BE.
     * `PAYMENT MODULE.docx` § 3/§ 4 sell Featured placement by a sealed weekly
     * auction — five Featured slots, two sponsored slots per category, bidding
     * every Friday 10:00–15:00 for the following week — and its parameters are
     * Docs/15 **N20–N24**. `bid_status` and `bidding_round_status` have existed
     * with no tables since 10 September and still do. An auction charges the
     * WINNER AT SETTLEMENT where v3 § 1 charges AT PURCHASE: a different shape,
     * not one smaller feature.
     *
     * ⚠️ AND THE SCOPE RULE POINTS THE OTHER WAY FROM USUAL HERE. Docs/15 Q81 is
     * *"if the client explicitly said no, do not build it; if it was merely
     * absent, build it"* — and the auction is **specified, not absent**. So a
     * fixed-price "buy Featured for a week" sold INSTEAD of it would contradict
     * his document rather than fill a gap. What is built is the
     * purchase-and-placement machinery the auction will sit on.
     *
     * WHAT THE SCREEN DOES: the admin-editable package catalogue hard rule 4
     * names explicitly (*"sponsored-slot pricing"*) over `products` /
     * `product_translations`, publish / unpublish / retire / restore with the
     * audit line and the read-back `@/lib/adminWrite` requires, the per-package
     * counts from `sponsored_placement_overview()` (an RPC that **raises** for a
     * non-admin, because a `count: 'exact'` over `purchases` from a browser is
     * RLS-filtered and renders as the platform's figure), and a keyset-paginated
     * list of what has been bought.
     *
     * 🔴 WHAT IT CANNOT DO, AND MUST NEVER GROW: it cannot touch a placement.
     * `purchases` has NO INSERT, UPDATE OR DELETE POLICY FOR ANYBODY including a
     * super admin, and since migration 075 the price, buyer, duration and window
     * are immutable **by trigger** as well — which binds `service_role` and the
     * table owner, whom RLS cannot see. A "cancel this placement" button would
     * issue an UPDATE matching zero rows, get HTTP 200 and show a green toast.
     * The lever that works is suspending the business in A-03;
     * `active_sponsored_placements()` filters on `businesses.status`. **Nothing
     * refunds it.**
     *
     * ⚠️ ONE PACKAGE IS SEEDED AND ITS NUMBERS ARE NOT ALL THE CLIENT'S.
     * `featured_business_7d`, NOK 299 gross for 7 days: the price is his N20
     * figure applied by the developer to a product he has not seen, and the
     * inclusive-of-MVA basis is the developer's alone. Docs/15 **Q143**, and
     * Docs/33 § 6.1 for the precedent where that same call was worth 25 % of a
     * price. Nothing in the UI implies he chose either.
     *
     * ⚠️ `roles` IS A REAL BOUNDARY FOR THE WRITES AND NOT FOR THE READS, as in
     * A-11. `products_select_admin` is a bare `is_admin()` — every admin role sees
     * the catalogue — while `products_insert_admin` / `products_update_admin` are
     * `is_admin(array['super_admin','finance'])`. So a `moderator`, `support` or
     * `content` operator sees every package and every write matches ZERO ROWS with
     * HTTP 200. ⚠️ The overview panel is the one thing that behaves differently
     * for them: it ERRORS, because the RPC raises. That asymmetry is correct.
     */
    keywords: ['slots', 'ads', 'inventory', 'calendar', 'campaigns', 'bidding'],
    built: true,
  },
  {
    id: 'plans',
    code: 'A-11',
    path: '/plans',
    icon: Layers,
    group: 'monetization',
    /*
     * ⚠️ `roles` IS A REAL BOUNDARY FOR THE WRITES AND NOT FOR THE READS, which
     * is a split neither A-19 nor A-20 has. `plans_select_admin` is a bare
     * `is_admin()` — every admin role reads the price list — while
     * `plans_insert_admin` / `plans_update_admin` are
     * `is_admin(array['super_admin','finance'])`. So a `moderator`, `support` or
     * `content` operator who navigates here sees every plan and every write
     * matches ZERO ROWS with HTTP 200. `useWritePermission` says so before they
     * type a price; `assertApplied` catches it if they type one anyway.
     */
    roles: ['super_admin', 'finance'],
    /*
     * 🔴 `built: true` FOR AN EDITOR THAT DELIBERATELY SUGGESTS NO NUMBERS.
     *
     * Hard rule 4 — "everything the admin could plausibly want to change must be
     * data, not code" — is only honoured if the admin can actually change it, and
     * until this screen existed a plan could only be created from SQL. What it
     * does NOT do is choose the numbers: `PAYMENT MODULE.docx` (21 Sep) said
     * NOK 499/month and two months free, `Proffplassen_Payment_Flow_v3.docx`
     * (6 Oct) REPLACED that document and names no price at all, and Docs/15 N21's
     * NOK 39 for Proffplassen+ is a third figure from a fourth conversation. So
     * nothing is seeded and no field in the editor carries a default — the same
     * decision migration 031 made in writing when it refused to seed
     * `platform.commission_percent`.
     *
     * ⚠️ THE TABLE IS NOT EMPTY, THOUGH, AND THIS COMMENT SAID IT WAS FOR AN HOUR.
     * Three `stripe_live_*` plans at 1 and 149 NOK were created by a parallel
     * session testing the Stripe integration on the same day, all three
     * published. They are test rows and not decisions, which is precisely why the
     * screen states "no price has been decided" rather than inferring it from a
     * row count.
     *
     * ⚠️ PUBLISHING A PLAN MAKES IT VISIBLE, NOT SELLABLE. `plans_select_public`
     * admits live rows to `anon`, so the website's pricing page shows it at once,
     * and whether a card can actually be charged is a separate question — the
     * only writer of `payments` is the server acting on a provider confirmation.
     * The screen says so above the table.
     *
     * Also absent, and stated on the screen rather than here: `products` (the
     * one-off Promotion / Boost packages) belong to A-10 and are still unbuilt;
     * `has_business_capability()` is untouched, so no feature is gated on a
     * subscription; and a `personal` plan created here is a price Apple and
     * Google still have to be told about by hand (v3 § 1 routes Proffplassen+
     * through in-app purchase).
     */
    keywords: ['pricing', 'subscriptions', 'stripe', 'trial', 'plans', 'tiers', 'abonnement'],
    built: true,
  },
  {
    id: 'payments',
    code: 'A-12',
    path: '/payments',
    icon: CreditCard,
    group: 'monetization',
    /*
     * Reads are a bare `is_admin()` — `payments_select`'s third arm, and
     * `finance` and `support` both legitimately need the ledger, since a support
     * agent answering "why was I charged" cannot do it blind. There are NO writes
     * at all, for anybody, so unlike A-11 this list is navigation only.
     */
    roles: ['super_admin', 'finance'],
    /*
     * 🔴 `built: true` FOR A LEDGER VIEW THAT IS READ-ONLY BY CONSTRUCTION.
     *
     * The client's own words are the specification: *"The monthly payment page
     * should be an overview of completed and failed payments, rather than one
     * invoice that becomes payable at month-end."* So there is no invoice, no
     * "mark as paid", no dunning run and no month-end close — and there must
     * never be: `payments` has NO INSERT, UPDATE OR DELETE POLICY FOR ANYBODY,
     * including a super admin, and RLS FILTERS. A button here would issue an
     * UPDATE matching zero rows, receive HTTP 200 and show a green toast for a
     * state change that never happened.
     *
     * ⚠️ WHAT IS IN THE LEDGER IS WHAT THE PROVIDER CONFIRMED, AND SEVERAL ROWS
     * ARE TEST DATA. `record_payment()` is the only writer and is revoked from
     * `authenticated`, so nothing a business or this panel can reach creates a
     * row. On 7 Oct a parallel session recorded eighteen test payments, some of
     * them in **XTS** — the ISO 4217 code reserved for testing, used so a test
     * amount cannot be summed into a real total. The monthly figures group BY
     * CURRENCY for exactly that reason, and the screen warns about it.
     *
     * ⚠️ DO NOT RECORD IN THIS FILE WHETHER THE WEBHOOK IS DEPLOYED. An earlier
     * version of this comment did, and it was wrong within the hour. Probe it.
     *
     * ⚠️ ALL SIX STATUSES THE CLIENT LISTED EXIST (`succeeded`, `pending`,
     * `failed`, `overdue`, `ready_for_collection`, `sent_to_collection` —
     * migration 066 added the last three for his list), AND THREE OF THEM CANNOT
     * APPEAR YET: `mark_payments_overdue()` reads its threshold from
     * `app_settings` and returns 0 while that row is absent, because v3 § 4 says
     * only "the agreed retry / access flow" and there is no agreement.
     *
     * ⚠️ AND "Trial" IS NOT A PAYMENT STATUS. A trial is the absence of a charge,
     * so the third of his three columns comes from `subscription_status.trialing`
     * — which is why this module has a second tab over `subscriptions`.
     */
    keywords: ['transactions', 'ledger', 'refunds', 'revenue', 'stripe', 'collection', 'trial'],
    built: true,
  },
  {
    id: 'payouts',
    /*
     * ⚠️ `A-28` IS A NEW CODE AND NOT A REUSED ONE. The spec stops at A-24 and
     * Topics / Claims / Submissions took A-25 / A-26 / A-27 from client documents
     * the spec predates; this comes from migrations 079 / 084 / 086, which the
     * spec also predates. 🔴 It is deliberately NOT `A-24`: that slot is RETIRED
     * rather than deleted (see the block below) and this client has reversed
     * himself twice in one month, so reusing the number would make a restored
     * Referrals module collide with a live Payouts one.
     */
    code: 'A-28',
    path: '/payouts',
    icon: Banknote,
    group: 'monetization',
    /*
     * 🔴 THIS IS A NAVIGATION BOUNDARY AND **NOT** A SECURITY ONE, WHICH IS THE
     * OPPOSITE OF A-19 AND A-20.
     *
     * Every dispute policy names `is_admin(array['super_admin','support'])`, so
     * A-20's `roles` and the database agree. Here `payout_requests_select`'s third
     * arm is a **bare `is_admin()`**, and `review_payout_request()` checks a bare
     * `is_admin()` too — no role array anywhere. So a `moderator`, `support` or
     * `content` operator who reaches this URL can read the queue AND decide a
     * payout, and nothing here or in the database stops them. Narrowing it
     * properly is a migration; recording it is not, and `data.ts` carries the same
     * note so that a narrow sidebar entry is never read as proof of a narrow
     * policy.
     */
    roles: ['super_admin', 'finance'],
    /*
     * 🔴 `built: true` FOR A QUEUE THAT CANNOT CONTAIN A ROW YET, AND THE
     * DISTINCTION IS THE SAME ONE A-10 DREW.
     *
     * The screen is real: `listPayoutQueueForAdmin` is keyset-paginated over the
     * live table, and approve / reject / mark-paid all go through
     * `review_payout_request()`, which writes its own `admin_audit_log` line in
     * the same transaction. ⚠️ **But nothing in the applied schema INSERTS into
     * `escrow_holds`** — no Edge Function, no RPC, no seed — so no hold has ever
     * been released, `business_escrow_balance()` is zero for every business, and
     * `request_payout()` refuses every amount as *"more than the 0 available"*.
     * The collection half of `Proffplassen_Payment_Flow_v3.docx` is the missing
     * piece, not this module. The note under the table says so, because an
     * operator opening an empty finance queue will otherwise assume it is broken.
     *
     * 🔴 WHAT IT CANNOT DO, AND MUST NEVER GROW: `payout_requests` has **no
     * INSERT, UPDATE OR DELETE POLICY FOR ANYBODY**, including a super admin, so
     * a form over the row would issue an UPDATE matching zero rows, receive HTTP
     * 200 and show a green toast for a payout nobody approved. And there is no
     * payout API behind "Paid": migration 079 built this WITHOUT Stripe Connect
     * at the developer's explicit direction, so `paid` is a human asserting that
     * they made a bank transfer elsewhere — which is exactly why the function
     * refuses it without the bank's own reference.
     *
     * ⚠️ `escrow_holds` IS NOT ADMINISTRABLE FROM ANY SCREEN EITHER. An admin can
     * `release_escrow()` by RPC and there is no `unrelease`, no refund and no
     * balance adjustment anywhere — so this module shows what was asked for and
     * what was decided, and nothing about the holds themselves.
     */
    keywords: ['payout', 'withdraw', 'escrow', 'bank', 'transfer', 'utbetaling', 'finance'],
    built: true,
  },
  /*
   * ─── A-24 REFERRALS & PROMO CODES — RETIRED 9 OCT 2026 ─────────────────────
   *
   * 🔴 REMOVED BECAUSE THE CLIENT NEVER ASKED FOR IT. That is the whole reason,
   * and it was found by looking rather than assumed: `referral`, `promo code`
   * and `discount` appear in **none** of his eleven documents — not the 10 Sep
   * brief, not the three 21 Sep plans, not the 22 Sep or 6 Oct feedback, not
   * `Proffplassen_Payment_Flow_v3.docx`. This module was a slot somebody added.
   *
   * ⚠️ The comment it replaces said *"blocked on N20–N24"*, and that was wrong
   * twice over: N20–N24 were all **answered on 23 September**, and none of them
   * is about referrals anyway. A blocker cited from memory outlived the thing it
   * named — the same failure this file's own `grep -c 'built: true'` warning is
   * about.
   *
   * The real obstacle was never a client answer. The payment module gives this
   * **no table at all** — no `referrals`, no `promo_codes`, no `credits` — and a
   * discount is not expressible against `plans.price_amount`, which is what a
   * subscription snapshots at sign-up with nothing beside it to reduce. So it
   * was a schema plus a product, for a feature nobody requested.
   *
   * ⚠️ RETIRED, NOT DELETED, and the distinction is load-bearing here: this
   * client has reversed himself **twice in one month** (Disputes refused in
   * writing then asked for back six days later; posting removed and returning as
   * "For the Community"). That is why no enum value has ever been dropped in
   * this schema and why `Docs/23` keeps restore tables.
   *
   * TO RESTORE: put this entry back with `built: false`, and keep
   * `permissions.ts`'s `'referrals'` capability, which is deliberately LEFT IN
   * PLACE — removing a capability from a role is a migration-shaped change for
   * no benefit, and leaving it costs nothing because no module claims it.
   */

  // ── Content ───────────────────────────────────────────────────────────────
  {
    id: 'categories',
    code: 'A-04',
    path: '/categories',
    icon: Tags,
    group: 'content',
    roles: ['super_admin', 'content'],
    keywords: ['taxonomy', 'tree', 'icons', 'seo', 'slug', 'nav'],
    built: true,
  },
  {
    id: 'services',
    code: 'A-05',
    path: '/services',
    icon: BookOpen,
    group: 'content',
    roles: ['super_admin', 'content'],
    /*
     * ⚠️ `service_templates`, NOT `services`. The module edits the PLATFORM's
     * suggested-service catalogue per category — Docs/08 A-05, "Service
     * templates per category" — not any business's own price list, which
     * belongs to the business and is governed by `has_business_capability`.
     * The two tables are one letter apart; see `features/services/data.ts`.
     */
    keywords: ['templates', 'taxonomy', 'duration', 'price type'],
    built: true,
  },
  {
    /*
     * Topics — the taxonomy behind hashtags, trending and the interests picker.
     *
     * NOT one of the 24 modules in Docs/08-SUPERADMIN-SPEC.md: it comes from the
     * client's 16 Sep FULL FUNCTION PLAN, which is still not accepted or priced
     * (Docs/19 § 7), so it is Phase 2 and carries the next free code rather than
     * renumbering the spec. `EXPECTED_MODULE_COUNT` below still counts the
     * spec's 24 on purpose — this row is not one of them.
     *
     * It sits beside Categories (A-04) because both are taxonomies the `content`
     * role owns, and includes `moderator` because blocking a topic is a
     * moderation act performed on this screen.
     */
    id: 'topics',
    code: 'A-25',
    path: '/topics',
    icon: Hash,
    group: 'content',
    roles: ['super_admin', 'content', 'moderator'],
    keywords: ['hashtags', 'trending', 'interests', 'tags', 'discover', 'taxonomy', 'social'],
    built: true,
  },
  {
    /*
     * Question / Fact / Quote of the Day — authoring and scheduling the Daily
     * carousel.
     *
     * 🔴 THIS MODULE IS A DEFECT REPORT, NOT A FEATURE REQUEST. On 12 Oct 2026
     * the client wrote *"Daily Questions, Quotes, and Facts are currently not
     * appearing on the Discover home screen."* He was right, and the cause was
     * the DATA: `supabase/seed/03-social.mjs` booked its three cards with
     * `scheduled_for = public.oslo_today()` **evaluated when the seed ran**, on
     * 22 September, so `daily_cards(null)` correctly returned `[]` for twenty
     * days. ⚠️ Nobody saw it because **nothing in this panel could show a
     * schedule, and nothing anywhere could author a card** — hard rule 4 broken
     * in the most literal way available: the content was only ever creatable by
     * running a seed script.
     *
     * `supabase/seed/09-daily-cards.mjs` put a rolling fortnight in and its own
     * header refuses to be the fix: *"the real fix is the admin module (A-29) …
     * A seed that silently kept topping itself up would hide the fact that
     * nobody can author one."* ⚠️ **THAT FORTNIGHT STILL RUNS OUT** — it was
     * seeded on 12 Oct and covers through 25 Oct. This screen's whole job is to
     * make that date visible before it arrives.
     *
     * NOT one of the 24 modules in Docs/08-SUPERADMIN-SPEC.md. Like A-25, A-26,
     * A-27 and A-28 it comes from a source the spec predates — here a client
     * defect report — so it takes the next free code rather than renumbering.
     * `SPEC_MODULE_COUNT` below still counts the spec's 24 on purpose.
     *
     * ⚠️ `A-29` AND NOT A REUSED NUMBER, for the reason A-28's comment gives:
     * A-24 Referrals is RETIRED rather than deleted, and this client has
     * reversed himself twice in one month.
     *
     * `roles` sits with Categories, Services and Topics because a daily card is
     * platform CONTENT — `author_kind = 'platform'`, the platform speaking in its
     * own name. `moderator` is included because withdrawing a badly worded card
     * is a moderation act performed on this screen. ⚠️ As almost everywhere in
     * this file the list is NAVIGATION, not the boundary:
     * `daily_questions_admin_write` and `posts_insert`'s platform branch are both
     * a bare `is_admin()`, so a `finance` operator who types the URL genuinely
     * can schedule a card. RLS is the boundary.
     */
    id: 'daily',
    code: 'A-29',
    path: '/daily',
    icon: CalendarDays,
    group: 'content',
    roles: ['super_admin', 'content', 'moderator'],
    /*
     * ⚠️ `built: true` FOR SCHEDULING AND AUTHORING, AND DELIBERATELY NOT FOR
     * EDITING A CARD'S WORDS. A daily card is an ordinary `posts` row — that is
     * migration 017's whole design, and it is what gives it votes, comments,
     * counters, reporting and moderation for free — so once it is published it
     * may carry answers. Rewriting the question under the answers belongs with
     * post moderation (A-07), not here, and the drawer says so where the control
     * would have been.
     *
     * ⚠️ AND IT ADDS NO MIGRATION, WHICH COSTS ONE THING WORTH KNOWING:
     * "write a card and schedule it" is three statements against three tables and
     * a browser cannot wrap them in a transaction. The post is created as a
     * `draft` and DELETED AGAIN if the booking is refused — a compensation, not
     * atomicity. The right answer is one `SECURITY DEFINER`
     * `create_and_schedule_daily_card()` RPC; it was not written because adding a
     * function means regenerating `packages/types/src/database.generated.ts`,
     * which a parallel session was editing. Recorded as owed work in
     * `features/daily/data.ts` rather than left to be rediscovered.
     *
     * 🔴 ONE FINDING FROM BUILDING IT, MEASURED AS ANON OVER HTTP AND NOT FIXED
     * HERE: the seed publishes all fourteen days of posts immediately, so **the
     * next fortnight of cards is publicly readable** — `GET
     * /daily_questions?scheduled_for=gte.<today>` as `anon` returns all 42 rows.
     * Migration 017 intends the opposite (*"tomorrow's question … must not leak
     * out"*). Cards authored from THIS screen are created `draft` and published
     * by `publish_scheduled_posts()` at Oslo midnight, so they do not leak; the
     * seeded ones still do, and that is `supabase/seed/`'s to fix.
     */
    keywords: [
      'daily',
      'question of the day',
      'quote',
      'fact',
      'carousel',
      'discover',
      'schedule',
      'calendar',
      'dagens',
      'sitat',
      'faktum',
    ],
    built: true,
  },
  {
    id: 'cms',
    code: 'A-13',
    path: '/cms',
    icon: FileText,
    group: 'content',
    roles: ['super_admin', 'content'],
    /*
     * ⚠️ `built: true` COVERS `cms_sections` AND `faqs` — TWO OF DOCS/08 A-13'S
     * FIVE SUB-FEATURES, and the other three are absent because the schema says
     * so rather than because the screen is unfinished. There is no `cms_pages`
     * table (a page in this product is a ROUTE with its own component, SEO and
     * lazy chunk); `cms_section_kind`'s nine values are all LAYOUTS, so there is
     * no prose kind and `legal_documents` is deferred until it can carry
     * `version`/`effective_from` for consent history; and the media library is
     * A-13's largest sub-feature with `config`/`media` as jsonb today. The blog
     * is `posts`, which has its own module. The screen names every one of these
     * where the missing control would have been.
     */
    keywords: ['homepage', 'pages', 'blog', 'faq', 'legal', 'media library', 'sections'],
    built: true,
  },
  {
    id: 'templates',
    code: 'A-14',
    path: '/templates',
    icon: Bell,
    group: 'content',
    roles: ['super_admin', 'content'],
    /*
     * ⚠️ `built: true` AND THE TABLE HAS ZERO ROWS. That is the platform's real
     * state, not the screen's: every notification this product has created
     * renders from no template. The coverage matrix is the point of the module.
     * Test-send is absent — `send-email` and `push-dispatch` both 404, and
     * `notifications` has no INSERT policy for a client to create the row a
     * delivery hangs off.
     */
    keywords: ['email', 'push', 'notifications', 'variables', 'preview'],
    built: true,
  },
  {
    id: 'broadcasts',
    code: 'A-15',
    path: '/broadcasts',
    icon: Megaphone,
    group: 'content',
    roles: ['super_admin', 'content'],
    /*
     * ⚠️ `built: true` MEANS IT COMPOSES CAMPAIGNS. NOTHING SENDS THEM.
     * `broadcasts` is a table with a status CHECK and no machinery: no trigger
     * or Edge Function reads it, so `sending`/`sent`/`failed` are shown and
     * never written from the panel, and there is no Send button. The audience
     * size is a count nobody can take (CLAUDE.md § 7) and `recipient_count` is
     * null on every row. Know this before demoing it.
     */
    keywords: ['campaigns', 'segments', 'newsletter', 'schedule', 'audience'],
    built: true,
  },
  {
    id: 'translations',
    code: 'A-16',
    path: '/translations',
    icon: Languages,
    group: 'content',
    roles: ['super_admin', 'content'],
    /*
     * ⚠️ `built: true` FOR A DELIBERATELY NARROWER MODULE THAN DOCS/08 A-16.
     * A-16's "table of every namespace.key" does not exist: the UI strings are
     * JSON files in the repository, compiled into each bundle, and
     * `pnpm i18n:check` enforces their parity in CI. What this module is
     * instead is the cross-cutting GAP FINDER over the three translation tables
     * that do exist — `category_translations`, `topic_translations`,
     * `service_template_translations` — answering the whole-set question none of
     * A-04, A-05 or A-25 can. The screen states the narrowing.
     */
    keywords: ['i18n', 'locales', 'nb', 'en', 'missing keys', 'strings'],
    built: true,
  },

  // ── Platform ──────────────────────────────────────────────────────────────
  {
    id: 'featureFlags',
    code: 'A-17',
    path: '/feature-flags',
    icon: ToggleLeft,
    group: 'platform',
    roles: ['super_admin'],
    /*
     * ⚠️ `built: true` AND THE TABLE HAS ZERO ROWS. That is the platform's real
     * state, not the screen's: nothing in the website, this panel or the mobile
     * app calls `feature_enabled()` yet, so a flag created here is a row nothing
     * reads. The screen says so. `feature_flags` is admin-only by policy —
     * `audience` holds targeting rules and RLS cannot hide a column — so no
     * client-facing read of the table exists or may be added.
     */
    keywords: ['toggles', 'rollout', 'experiments', 'environments'],
    built: true,
  },
  {
    id: 'settings',
    code: 'A-18',
    path: '/settings',
    icon: Settings2,
    group: 'platform',
    roles: ['super_admin'],
    /*
     * ⚠️ `built: true` WITH NO CREATE AND NO DELETE, AND THAT IS THE SCHEMA'S
     * DECISION. `app_settings` has no DELETE policy at all, and migration 031 is
     * explicit: a key is authored by the code that reads it, so adding or
     * removing one is a migration — "the same act as adding one, and not a
     * button". Nor is `value_type` editable: it is the contract the reader
     * compiled against. `is_public` is the table's whole access model and the
     * most dangerous control in this panel, so publishing demands a typed
     * confirmation and a reason.
     */
    keywords: ['config', 'limits', 'maintenance', 'ranking', 'app version', 'referral'],
    built: true,
  },
  {
    id: 'analytics',
    code: 'A-21',
    path: '/analytics',
    icon: BarChart3,
    group: 'platform',
    roles: ['super_admin', 'finance'],
    /*
     * ⚠️ `built: true` ON FOUR SECURITY DEFINER RPCs (migrations 035/036), WITH
     * NO REVENUE AND NO CSV EXPORT.
     *
     * There is no billing anywhere in this schema — no plans, no subscriptions,
     * no payments — so MRR, ARPU and GMV cannot be measured, only invented. They
     * render as "not built", never as 0, exactly as `admin_dashboard_kpis`
     * omits them. CSV export is deliberately deferred: a CSV of a top-N table
     * that does not carry its caveat is the same number leaving the building
     * without it.
     *
     * ⚠️ AND THE FUNNEL'S TAIL IS PERMANENTLY IMMATURE. It is a cohort funnel —
     * the cohort is requests published inside the window, and the later steps
     * take real-world days — so a short window understates them by construction.
     * The screen marks those steps rather than hiding the problem in a longer
     * default window.
     */
    keywords: ['reports', 'funnel', 'cohorts', 'retention', 'league table', 'csv'],
    built: true,
  },
  {
    id: 'adminTeam',
    code: 'A-22',
    path: '/admin-team',
    icon: UserCog,
    group: 'platform',
    roles: ['super_admin'],
    /*
     * ⚠️ `built: true` FOR A SCREEN THAT CANNOT WORK UNTIL SOMEBODY DEPLOYS AN
     * EDGE FUNCTION, AND THAT IS NOT THE SAME AS UNFINISHED.
     *
     * `admin_users` has no INSERT, UPDATE or DELETE policy — deliberately, because
     * `is_admin()` in a write policy is a bootstrap deadlock and anything weaker is
     * a privilege-escalation hole — and RLS FILTERS. So this module is the one in
     * the panel that CANNOT be built against its table: every write would answer
     * 2xx with zero rows and a success toast. It talks to `admin-team` instead.
     *
     * That function is written and tested and has **never been deployed** — there
     * is no Supabase CLI on this machine — so every call answers 404 from the
     * functions gateway today. The screen tells that apart from a failure and says
     * so by name, with the command. `built: false` would have been the wrong flag:
     * it hides the module in the client demo and routes to "not built yet", which
     * describes neither the code nor the reason.
     *
     * ⚠️ `roles: ['super_admin']` IS NAVIGATION, NOT THE BOUNDARY — as everywhere
     * else in this file. `requireSuperAdmin` in the function is the boundary, and it
     * reads `admin_users` with the service role rather than trusting `is_admin()`,
     * which knows nothing about `deleted_at`.
     */
    keywords: ['staff', 'roles', 'permissions', 'invite', 'mfa'],
    built: true,
  },
  {
    id: 'auditLog',
    code: 'A-23',
    path: '/audit-log',
    icon: FileClock,
    group: 'platform',
    roles: ['super_admin'],
    /*
     * ⚠️ `built: true` MEANS A READER, AND IT CANNOT BE ANYTHING ELSE.
     * `admin_audit_log` is append-only enforced TWICE — no UPDATE or DELETE
     * policy closes PostgREST, and `admin_audit_log_append_only` raises for the
     * service role and the table owner, whom RLS cannot see. So there is no
     * edit, no delete and no retention purge on the screen; each would report
     * success while doing nothing.
     *
     * ⚠️ AND ONLY FOUR MODULES WRITE TO IT. Settings, feature flags, CMS
     * sections and FAQs record who and why; the eleven modules built before
     * migration 033 existed do not, so suspending a business still records what
     * and not by whom. The screen says so rather than letting a short log read
     * as a quiet week.
     */
    keywords: ['history', 'trail', 'diff', 'compliance', 'who did what'],
    built: true,
  },
] as const

/**
 * Sanity check the module count so a deletion cannot slip through.
 *
 * 23 of these are modules in Docs/08-SUPERADMIN-SPEC.md. The 24th is Topics
 * (A-25), from the client's 16 Sep FULL FUNCTION PLAN — Phase 2, not in the
 * spec and not in the USD 1,600 scope (Docs/19). The 25th and 26th are Business
 * claims (A-26) and Business submissions (A-27), the two halves of the admin
 * queue in the client's 26 Sep clarifications (Docs/29 § 2), which the spec also
 * predates. The constant counts what is in THIS FILE, which is what makes it
 * useful as a deletion guard; the spec's own count is documented on each of
 * those three rows.
 *
 * ⚠️ 28 AS OF 12 OCT: A-29 Daily added. It is NOT a spec module either, so
 * `SPEC_MODULE_COUNT` is unchanged — and the gap between the two constants is now
 * five (A-25, A-26, A-27, A-28, A-29), every one of them from a client document
 * or report the spec predates. **Re-derive both from `^    id: '` and this
 * file's rows rather than reading the numbers here**, which is the same warning
 * the `built: true` block below carries: a bare grep has been wrong in the same
 * direction every single time anyone has tried it.
 *
 * ⚠️ WENT 27 → 26 ON 1 OCT AND BACK TO 27 ON 6 OCT. A-20 Disputes was removed on
 * the understanding that the client had rejected it in writing (Docs/22 § 3,
 * Docs/15 Q81) and restored when the October review asked for it twice — § 2.3
 * puts it on page 2 of Support, § 2.4 is headed "Disputes – Needs to Be Built".
 * A number going DOWN is the one direction that is not automatically a bug; this
 * is the only time it has, and the only time it has come back.
 */
export const EXPECTED_MODULE_COUNT = 28

/**
 * Of the above, how many the contracted spec accounts for.
 *
 * Back to 24: Docs/08 specifies A-20 Disputes, and this panel offers it again as
 * of 6 Oct. It was 23 for five days, while we believed the client had refused it.
 * Topics (A-25), Business claims (A-26) and Business submissions (A-27) are the
 * three that are NOT in the spec, which is why this is 24 and not 27.
 */
export const SPEC_MODULE_COUNT = 24

/**
 * Does this build offer the modules that are not finished?
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 SAFE BY DEFAULT, AND IT WAS NOT. This used `filterForDeployment` from
 * `@avexa/config/demo-mode`, whose rule is "hide unfinished work **only** when
 * `VITE_DEMO_MODE` is truthy". `VITE_DEMO_MODE` is not set on the Netlify site,
 * so the then-four unbuilt monetization modules — A-10 sponsored, A-11 plans,
 * A-12 payments, A-24 referrals, every one blocked on N20–N24 — were listed in
 * the sidebar and offered by the command palette **on the client's own panel**.
 * That is exactly the menu of empty rooms the flag exists to prevent, and it took
 * an unset variable to produce it.
 *
 * ⚠️ "FOUR" WENT TO TWO ON 7 OCT (A-11 and A-12) AND TO **ONE** ON 8 OCT (A-10).
 * The only unbuilt module left is **A-24 referrals**, which the payment module
 * gave no table at all. Re-derive the figure from `^    built: false,` in this
 * file rather than reading it here — every count this project has written down in
 * prose has gone stale within days, and 🔴 `grep -c 'built: true'` ANSWERS 43
 * AGAINST THE REAL 26 because sixteen of the hits are in comments. **The anchored
 * pattern is the count; a bare grep is a hypothesis.**
 *
 * ⚠️ THE DEFAULT NOW FOLLOWS THE BUILD, NOT A VARIABLE. A dev server is where
 * half-built screens are worked on, so they are all reachable there. A BUILT
 * bundle is something somebody was given a link to, so it shows finished work
 * only — no environment variable required to be correct.
 *
 * Explicit still wins, in both directions, which is the escape hatch for a
 * Netlify Deploy Preview that is meant to show work in progress:
 *
 *   VITE_DEMO_MODE=true   hide unfinished modules (the original meaning)
 *   VITE_DEMO_MODE=false  show them, even in a built bundle
 *   unset                 hidden in a build, shown on a dev server
 *
 * ⚠️ `packages/config/src/demo-mode.ts` IS DELIBERATELY UNTOUCHED — it is shared
 * with `apps/web`, and widening its default there is a separate decision for
 * whoever owns that package. `isDemoMode()` is still what `AdminShell` reads for
 * the badge, because a truthy flag is an explicit statement and needs no
 * reinterpretation.
 *
 * NOT A SECURITY BOUNDARY, unchanged: the ROUTES still resolve, so anyone typing
 * a path reaches the placeholder. This hides navigation. RLS protects data.
 * ═══════════════════════════════════════════════════════════════════════════
 */
function offersUnbuiltModules(): boolean {
  const configured = import.meta.env.VITE_DEMO_MODE?.trim()

  if (configured !== undefined && configured !== '') {
    // The same truthiness `demo-mode.ts` uses, so the two cannot disagree about
    // what the flag means when it IS set.
    return !/^(true|1|yes|on)$/i.test(configured)
  }

  return import.meta.env.DEV
}

/**
 * The modules this deployment should actually offer — the sidebar and the
 * command palette both read this and never `MODULES`.
 */
export const VISIBLE_MODULES: readonly AdminModule[] = offersUnbuiltModules()
  ? [...MODULES]
  : MODULES.filter((module) => module.built)

export const MODULES_BY_GROUP: ReadonlyArray<{
  group: NavGroup
  modules: readonly AdminModule[]
}> = NAV_GROUPS.map((group) => ({
  group,
  modules: VISIBLE_MODULES.filter((module) => module.group === group),
})).filter((entry) => entry.modules.length > 0)

export function findModuleByPath(pathname: string): AdminModule | undefined {
  if (pathname === '/') return MODULES.find((module) => module.path === '/')
  return MODULES.filter((module) => module.path !== '/').find(
    (module) => pathname === module.path || pathname.startsWith(`${module.path}/`),
  )
}

/** The icon used by the dashboard's "needs attention" panel and stat cards. */
export const QUEUE_ICON: LucideIcon = Gauge
